/*                                                                                                                     
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--'

Copyright (c) 2026 Dynamic IOT Data, Inc. All rights reserved.

@author Andrew Craig
*/
import { Measurement } from '../../components/measurement';
import { VisionApplication } from '../../application';
import { TimeSelect } from '../../components/timeselect';
import { PublishTopic } from '../../events';
import { SVGIcons } from '../../map/svg';
import { IPubSubSubscriber } from '../../pubsub';
import { AssetDTO, MotionState } from '../../service/asset.js';
import { FolderDTO, AssetFolderService, FolderItemDTO } from '../../service/folder';
import { AssetTelemetry, SpreaderTelemetry } from '../../service/latest';
import { VisionAssetWindow } from '../../window/asset';
import { VisionAVLTab2 } from './avl_tab';

/** Handles the Asset Tree on the AVL tab.
 *
 */
export class AssetTree implements IPubSubSubscriber {
    nullUUID = '00000000-0000-0000-0000-000000000000';

    // UI Elements
    parent: VisionAVLTab2;
    toolbar!: dhtmlXToolbarObject;
    tree!: dhtmlXGridObject;
    contextMenu?: dhtmlXMenuObject;

    // Caches
    folderCache: Map<string, FolderDTO>;

    // Services
    assetFolderService: AssetFolderService;

    identifier = 'da50483b-b5e6-43ac-87d8-8938b51a5821';
    activeFolder!: string;

    readonly columns = [
        { label: 'Vehicle', id: 'col_vehicle', type: 'tree', align: 'text-align:left;', col: 'left', width: '200', idx: '0', parent: 'telemetry', sort: 'int' },
        { label: 'Status', id: 'col_status', type: 'ron', align: 'text-align:center;', col: 'center', width: '*', idx: '1', parent: 'telemetry', sort: 'str' },
        { label: 'Speed', id: 'col_speed', type: 'ron', align: 'text-align:center;', col: 'center', width: '*', idx: '2', parent: 'telemetry', sort: 'int' },
        { label: 'Odometer', id: 'col_odometer', type: 'ron', align: 'text-align:center;', col: 'center', width: '*', idx: '3', parent: 'telemetry', sort: 'int' },
        { label: 'Hours', id: 'col_hours', type: 'ron', align: 'text-align:center;', col: 'center', width: '*', idx: '4', parent: 'telemetry', sort: 'int' },
        { label: 'Input 1', id: 'col_input1', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '5', parent: 'inputs', sort: 'int' },
        { label: 'Input 2', id: 'col_input2', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '6', parent: 'inputs', sort: 'int' },
        { label: 'Input 3', id: 'col_input3', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '7', parent: 'inputs', sort: 'int' },
        { label: 'Input 4', id: 'col_input4', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '8', parent: 'inputs', sort: 'int' },
        { label: 'Last Seen', id: 'col_last_seen', type: 'txt', align: 'text-align:center;', col: 'center', width: '175', idx: '9', parent: 'telemetry', sort: 'int' },
        { label: 'Description', id: 'col_description', type: 'txt', align: 'text-align:center;', col: 'center', width: '100', idx: '10', parent: 'telemetry', sort: 'str' },
        { label: 'Plowing', id: 'col_plow', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '11', parent: 'activities', sort: 'int' },
        { label: 'Camera', id: 'col_camera', type: 'img', align: 'text-align:center;', col: 'center', width: '*', idx: '12', parent: 'activities', sort: 'int' },
        { label: 'Analog 1', id: 'col_analog1', type: 'ron', align: 'text-align:center;', col: 'center', width: '*', idx: '13', parent: 'inputs', sort: 'int' },
        { label: 'Analog 2', id: 'col_analog2', type: 'ron', align: 'text-align:center;', col: 'center', width: '*', idx: '14', parent: 'inputs', sort: 'int' },
        { label: 'Analog 3', id: 'col_analog3', type: 'ron', align: 'text-align:center;', col: 'center', width: '*', idx: '15', parent: 'inputs', sort: 'int' },
        { label: 'Analog 4', id: 'col_analog4', type: 'ron', align: 'text-align:center;', col: 'center', width: '*', idx: '16', parent: 'inputs', sort: 'int' },
        { label: 'Spreader', id: 'col_spreader', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '17', parent: 'activities', sort: 'int' },
        { label: 'Mowing', id: 'col_mowing', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '18', parent: 'activities', sort: 'int' },
        { label: 'Grading', id: 'col_grading', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '19', parent: 'activities', sort: 'int' },
        { label: 'Rocking', id: 'col_rocking', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '20', parent: 'activities', sort: 'int' },
        { label: 'Spreader Status', id: 'col_spreader_status', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '21', parent: 'spreader', sort: 'int' },
        { label: 'Solid Set', id: 'col_spreader_solid_set', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '22', parent: 'spreader', sort: 'int' },
        { label: 'Solid Act', id: 'col_spreader_solid_act', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '23', parent: 'spreader', sort: 'int' },
        { label: 'Solid Mode', id: 'col_spreader_solid_mode', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '24', parent: 'spreader', sort: 'int' },
        { label: 'Prewet Set', id: 'col_spreader_prewet_set', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '25', parent: 'spreader', sort: 'int' },
        { label: 'Prewet Act', id: 'col_spreader_prewet_act', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '26', parent: 'spreader', sort: 'int' },
        { label: 'Prewet Mode', id: 'col_spreader_prewet_mode', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '27', parent: 'spreader', sort: 'int' },
        { label: 'Antiice Set', id: 'col_spreader_antiice_set', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '28', parent: 'spreader', sort: 'int' },
        { label: 'Antiice Act', id: 'col_spreader_antiice_act', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '29', parent: 'spreader', sort: 'int' },
        { label: 'Antiice Mode', id: 'col_spreader_antiice_mode', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '30', parent: 'spreader', sort: 'int' },
        { label: 'Solid Material', id: 'col_spreader_solid_material', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '31', parent: 'spreader', sort: 'str' },
        { label: 'Prewet Material', id: 'col_spreader_prewet_material', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '32', parent: 'spreader', sort: 'str' },
        { label: 'Antiice Material', id: 'col_spreader_antiice_material', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '33', parent: 'spreader', sort: 'str' },
        { label: 'Seasonal Solid 1', id: 'col_spreader_seasonal_solid_1', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '34', parent: 'spreader', sort: 'int' },
        { label: 'Seasonal Solid 2', id: 'col_spreader_seasonal_solid_2', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '35', parent: 'spreader', sort: 'int' },
        { label: 'Seasonal Prewet 1', id: 'col_spreader_seasonal_prewet_1', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '36', parent: 'spreader', sort: 'int' },
        { label: 'Seasonal Prewet 2', id: 'col_spreader_seasonal_prewet_2', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '37', parent: 'spreader', sort: 'int' },
        { label: 'Seasonal Antiice 1', id: 'col_spreader_seasonal_antiice_1', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '38', parent: 'spreader', sort: 'int' },
        { label: 'Seasonal Antiice 2', id: 'col_spreader_seasonal_antiice_2', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '39', parent: 'spreader', sort: 'int' },
        { label: 'Address', id: 'col_address', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '40', parent: 'telemetry', sort: 'str' },
        { label: 'Posted', id: 'col_posted', type: 'txt', align: 'text-align:center;', col: 'center', width: '*', idx: '41', parent: 'telemetry', sort: 'str' },
    ];

    constructor(parent: VisionAVLTab2) {
        this.parent = parent;

        this.folderCache = new Map<string, FolderDTO>();
        this.assetFolderService = new AssetFolderService();

        this.tree = this.parent.tabbar.cells('ASSET').attachGrid();

        this.parent.layout.cells('b').setText('Assets');
        const build = this.buildGridHeader();

        this.tree.setHeader(build.header, '', build.align);
        this.tree.setColTypes(build.types);
        this.tree.setColumnIds(build.columns);
        this.tree.setColAlign(build.col);
        this.tree.setInitWidths(build.widths);
        this.tree.enableResizing(build.resize);
        this.tree.setColSorting(build.colSort);

        this.tree.setImagesPath('/css/imgs/');
        this.tree.setSizes();
        this.tree.enableColumnMove(true, undefined);
        this.tree.setEditable(false);

        this.tree.init();

        // Hide all columns
        this.columns.forEach((value) => {
            this.tree.setColumnHidden(Number(value.idx), true);
        });

        // Turn on some defaults.
        this.tree.setColumnHidden(0, false); // Vehicle Name
        this.tree.setColumnHidden(1, false); // Status
        this.tree.setColumnHidden(2, false); // Speed
        this.tree.setColumnHidden(9, false); // Last Seen

        // Sets up some custom sorting for the tree.
        this.tree.setCustomSorting((_a, _b, ord, aid, bid) => {
            const aName = this.tree.getUserData(aid, 'name');
            const bName = this.tree.getUserData(bid, 'name');

            return VisionApplication.nameSort(aName, bName, ord);
        }, 0);
        this.tree.setCustomSorting((_a1, _b1, ord, aid, bid) => {
            const a = this.tree.getUserData(aid, 'timestamp');
            const b = this.tree.getUserData(bid, 'timestamp');
            return ord == 'asc' ? (a > b ? 1 : -1) : a > b ? -1 : 1;
        }, 9);
        this.tree.setCustomSorting((_a1, _b1, ord, aid, bid) => {
            const a = this.tree.getUserData(aid, 'state');
            const b = this.tree.getUserData(bid, 'state');
            return ord == 'asc' ? (a > b ? 1 : -1) : a > b ? -1 : 1;
        }, 1);

        // Set up the context menu on right click on asset
        this.contextMenu = new dhtmlXMenuObject();
        this.contextMenu.setIconsPath('css/imgs/');
        this.contextMenu.renderAsContextMenu();

        this.contextMenu.loadStruct(`<menu id="0">                        
                        <item text="Track Today" id="track_today"/>
                        <item text="Track Yesterday" id="track_yesterday"/>
                        <item text="Edit" id="edit"/>
                        </menu>`);

        this.tree.enableContextMenu(this.contextMenu);

        this.toolbar = this.parent.tabbar.cells('ASSET').attachToolbar();
        this.toolbar.addInput('input_search', 0, '', 100);
        this.toolbar.setItemToolTip('input_search', 'Search name/description');
        this.toolbar.addButtonSelect('asset_folder', 1, 'Groupings', [], '', '', false, false, 0, '');
        this.toolbar.setIconsPath('/css/imgs/');
        this.toolbar.addButtonTwoState('assets_on_map', 3, 'On Map', '', '');
        this.toolbar.addSpacer('assets_on_map');
        this.toolbar.addButtonSelect('workspaces', 4, 'Workspaces', [], '', '', false, false, 0, '');
        this.toolbar.addSpacer('workspaces');
        this.toolbar.addButton('clear_details', 5, 'Clear', '', '');
        this.toolbar.addButton('configure_columns', 6, 'Columns', '', '');
        this.toolbar.addButton('fit', 7, 'Fit', '', '');

        this.parent.app.pubsub.subscribe(PublishTopic.UPDATE_ASSET_TELEMETRY, this);
        this.parent.app.pubsub.subscribe(PublishTopic.WORKSPACES_RELOADED, this);
        this.parent.app.pubsub.subscribe(PublishTopic.OOS_STATUS_CHANGED, this);
        this.parent.app.pubsub.subscribe(PublishTopic.ASSET_INFO_UPDATED, this);

        this.setupEvents();
        this.getFolders();
        this.populateWorkspaces();

        this.selectWorkspace(localStorage.getItem('selected_workspace'));
    }

    private populateWorkspaces() {
        this.toolbar.forEachListOption('workspaces', (id) => {
            this.toolbar.removeListOption('workspaces', id);
        });

        this.parent.app.workspaceService.workspaces.forEach((value, key) => {
            if (key && value.name) {
                this.toolbar.addListOption('workspaces', 'WS_' + key, 1, 'button', value.name, '');
                this.toolbar.clearListOptionImage('workspaces', 'WS_' + key);
            }
        });
    }

    /**
     * Builds the dropdown list for the available folders.
     */
    private getFolders() {
        const folderRequest = this.assetFolderService.getFolders();
        folderRequest.then((data) => {
            if (data.parsedBody) {
                //console.log(data.parsedBody);
                for (let i = 0; i < data.parsedBody?.length; i++) {
                    this.folderCache.set(data.parsedBody[i].id, data.parsedBody[i]);
                    this.toolbar.addListOption('asset_folder', data.parsedBody[i].id, 1, 'button', data.parsedBody[i].name, '');
                    this.toolbar.clearListOptionImage('asset_folder', data.parsedBody[i].id);
                }

                const selectedGroup = localStorage.getItem('selected_folder');
                if (selectedGroup != null) {
                    this.folderCache.forEach((group) => {
                        if (group.id == selectedGroup) {
                            this.selectFolder(group.id);
                        }
                    });
                } else {
                    const firstEntry = this.folderCache.entries().next().value;
                    if (firstEntry) {
                        const [key, _folder] = firstEntry;
                        this.selectFolder(key);
                    }
                }

                if (this.parent.app.userService.treeSortDirection != null) {
                    this.tree.sortRows(0, '', this.parent.app.userService.treeSortDirection);
                } else {
                    this.tree.sortRows(0, 'int', 'asc');
                }

                if (this.parent.app.latestService) {
                    this.parent.app.latestService.lastUpdateTime = 0;
                    this.parent.app.latestService.currentFolderId = localStorage.getItem('selected_folder');
                    this.parent.app.latestService.processAssetTelemetry(true);
                }
            }
        });

        /*
        folderRequest.then((data) => {
            if (data.parsedBody) {
                for (let i = 0; i < data.parsedBody?.length; i++) {
                    this.folderCache.set(data.parsedBody[i].id, data.parsedBody[i]);
                    this.toolbar.addListOption('asset_folder', data.parsedBody[i].id, 1, 'button', data.parsedBody[i].name, '');
                    this.toolbar.clearListOptionImage('asset_folder', data.parsedBody[i].id);
                    if (this.parent.app.userService.selectedFolder != null) {
                        this.selectFolder(this.parent.app.userService.selectedFolder);
                    } else {
                        this.selectFolder(data.parsedBody[i].id);
                    }

                    if (this.parent.app.userService.selectedWorkspace != null) {
                        this.selectWorkspace(this.parent.app.userService.selectedWorkspace);
                    }

                    if (this.parent.app.userService.treeSortDirection != null) {
                        this.tree.sortRows(0, '', this.parent.app.userService.treeSortDirection);
                    } else {
                        this.tree.sortRows(0, 'int', 'asc');
                        this.selectFolder(data.parsedBody[i].id);
                    }

                    if (this.parent.app.latestService) {
                        this.parent.app.latestService.lastUpdateTime = 0;
                        this.parent.app.latestService.currentFolderId = localStorage.getItem('selected_folder');
                        this.parent.app.latestService.processAssetTelemetry(false);
                    }
                }
            }
        });
        */
    }

    /** User has selected a folder from the pull down list.
     *  Publishes a FOLDER SWITCH message
     *  Saves in localstorage the selected folder
     *
     * @param folderId The UUID of the folder to select.
     */
    selectFolder(folderId: string): void {
        if (this.activeFolder == folderId) {
            return;
        }
        this.activeFolder = folderId;

        const name = this.toolbar.getListOptionText('asset_folder', folderId);
        this.buildFolderTree(this.folderCache.get(folderId));
        this.parent.app.pubsub.publish('FOLDER_SWITCH', folderId);

        localStorage.setItem('selected_folder', folderId);
        this.parent.layout.cells('b').setText(name);

        if (this.parent.mapPanel.cluster) {
            this.parent.mapPanel.cluster.clearMarkers();
        }
    }

    private setupEvents() {
        /** Tree Context Events */
        this.tree.attachEvent('onAfterSorting', (_idx, _type, direction) => {
            localStorage.setItem('tree_sort_direction', direction);
        });

        this.tree.attachEvent('onRowSelect', (id, idx) => {
            const colId = this.tree.getColumnId(idx);
            if (colId == 'col_camera') {
                this.showCameraWindow(id);
            }
        });

        this.tree.attachEvent('onBeforeContextMenu', (rowId, cellInd, _grid) => {
            return this.tree.getUserData(rowId, 'type') != 'group' && cellInd == 0;
        });

        this.tree.attachEvent('onRowDblClicked', (rowId, _cellInd) => {
            if (this.tree.getUserData(rowId, 'type') != 'group') {
                const assetid = rowId;

                let zoom = 15;
                try {
                    zoom = VisionApplication.getInstance().userService.settings.defaulltZoom;
                } catch (e) {
                    zoom = 15;
                }
                if (zoom == undefined) {
                    zoom = 15;
                }

                this.parent.mapPanel.zoomToLevel(assetid, zoom);
                const marker = this.parent.mapPanel.markerCache.get(assetid);

                if (marker) {
                    marker.highlight = true;
                    marker.drawToCanvas();
                    marker.updateIcon();

                    setTimeout(() => {
                        if (marker) {
                            marker.highlight = false;
                            marker.drawToCanvas();
                            marker.updateIcon();
                        }
                    }, 5000);
                }
            }
        });

        this.contextMenu?.attachEvent('onClick', (menuItemId, _type) => {
            const assetid = this.tree.contextID.slice(0, -2);
            switch (menuItemId) {
                case 'track_today': {
                    const track = this.parent.getTrackingWindow();
                    track.show();
                    track.setTime(TimeSelect.TODAY);
                    track.addAsset(assetid);
                    break;
                }

                case 'track_yesterday': {
                    const track = this.parent.getTrackingWindow();
                    track.show();
                    track.setTime(TimeSelect.YESTERDAY);
                    track.addAsset(assetid);
                    break;
                }

                case 'trip': {
                    const trip = this.parent.getTripWindow();
                    trip.show();
                    break;
                }

                case 'edit': {
                    this.editAsset(assetid);
                    break;
                }
            }
        });

        const input = this.toolbar.getInput('input_search');
        input.onkeyup = () => {
            const value = this.toolbar.getValue('input_search');
            console.log('VALUE IS: ' + value);
            this.filterTree(value);
        };

        this.toolbar.attachEvent('onStateChange', (id, state) => {
            switch (id) {
                case 'assets_on_map': {
                    this.parent.app.pubsub.publish(PublishTopic.ONLY_SHOW_ON_MAP, state);
                    break;
                }
            }
        });
        this.toolbar.attachEvent('onClick', (itemId) => {
            console.log('Toolbar Click ' + itemId);

            if (itemId.startsWith('WS_')) {
                const workspaceId = itemId.split('_')[1];
                this.selectWorkspace(workspaceId);
            } else {
                switch (itemId) {
                    case 'workspaces': {
                        break;
                    } // This is the select itself

                    case 'config': {
                        break;
                    }
                    case 'expand_tree': {
                        console.log('Expanding Tree');
                        this.tree.expandAll();
                        break;
                    }
                    case 'asset_folder': {
                        break;
                    }
                    case 'clear_details': {
                        break;
                    }
                    case 'configure_columns': {
                        this.selectColumns();
                        break;
                    }
                    case 'fit': {
                        this.tree.setSizes();
                        for (let i = 0; i < this.columns.length; i++) {
                            this.tree.adjustColumnSize(i);
                        }

                        break;
                    }
                    default: {
                        this.selectFolder(itemId);
                        break;
                    }
                }
            }
        });
    }

    private buildGridHeader() {
        /*
        let header: string[];
        let types: string[];
        let ids: string[];
        let align: string[];
        let col: string[]; // Column data alignment
        let widths: string[];
        */

        const header: string[] = [];
        const types: string[] = [];
        const ids: string[] = [];
        const align: string[] = [];
        const col: string[] = [];
        const widths: string[] = [];
        const resize: string[] = [];
        const colSort: string[] = [];

        this.columns.forEach((value) => {
            header.push(value.label);
            types.push(value.type);
            ids.push(value.id);
            align.push(value.align);
            col.push(value.col);
            //widths.push(value.width);
            widths.push('100');
            resize.push('true');
            colSort.push(value.sort);
        });
        return {
            header: header.join(','),
            types: types.join(','),
            columns: ids.join(','),
            align: align,
            col: col.join(','),
            widths: widths.join(','),
            resize: resize.join(','),
            colSort: colSort.join(','),
        };
    }

    private filterTree(value: string): void {
        this.tree.forEachRow((id) => {
            this.tree.setRowHidden(id, false);
            const marker = this.parent.mapPanel.markerCache.get(id);
            if (marker) {
                marker.marker.setMap(this.parent.mapPanel.map.map);
            }
        });

        const description_col = this.tree.getColIndexById('col_description');
        this.tree.forEachRow((id) => {
            const name = this.tree.getUserData(id, 'name');
            const desc = this.tree.cells(id, description_col).getValue();

            if (name != null && name.search(new RegExp(value, 'i')) == -1 && desc.search(new RegExp(value, 'i')) == -1) {
                this.tree.setRowHidden(id, true);
                const marker = this.parent.mapPanel.markerCache.get(id);
                if (marker) marker.marker.setMap(null);
            }
        });
    }

    private buildFolderTree(data: FolderDTO | undefined) {
        if (data == undefined) {
            return;
        }
        this.tree.clearAll(false);
        console.log('Building Tree');

        let i = 0;
        const branches = [...data.branches];
        const organizedList = new Array<string>();
        const mapped = new Map<string, FolderItemDTO>();
        data.branches?.forEach((groupData) => {
            mapped.set(groupData.id, groupData);
        });

        // Organize the branch list to make sure that the parent is always added before the child.
        while (branches.length > 0) {
            const groupData = branches.shift();
            if (groupData) {
                // If no parent than we are good.
                if (groupData.parent == this.nullUUID) {
                    if (organizedList.includes(groupData.id) == false) {
                        organizedList.push(groupData.id);
                    }
                } else {
                    // If we have a parent and in the list ok,  if not push into the end of the list.
                    if (organizedList.includes(groupData.parent)) {
                        organizedList.push(groupData.id);
                    } else {
                        branches.push(groupData);
                    }
                }
            }
        }

        organizedList?.forEach((id) => {
            const groupData = mapped.get(id);
            if (groupData) {
                const id = groupData.parent == this.nullUUID ? '' : groupData.parent;
                console.log('Adding Group');
                this.tree.addRow(groupData.id, [groupData.name], 0, id, 'blank.gif', true);
                this.tree.setUserData(groupData.id, 'type', 'group');
            }
        });

        data.items?.forEach((assetData) => {
            this.tree.addRow(assetData.id, [assetData.name, '', '', '', ''], i++, assetData.parent, 'blank.gif', false);
            this.tree.setUserData(assetData.id, 'type', 'asset');
            this.tree.setUserData(assetData.id, 'state', 99);

            if (assetData.name.startsWith('#')) {
                this.tree.setUserData(assetData.id, 'name', assetData.name.substring(1));
            } else {
                this.tree.setUserData(assetData.id, 'name', assetData.name);
            }
        });

        this.tree.expandAll();

        // Seems to cause a problem
        // this.tree.adjustColumnSize(0);
    }

    /**
     *
     * @param msg Mssage type.  See PublishTopic
     * @param data The published data
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    notify(msg: string, data?: any): void {
        switch (msg) {
            case PublishTopic.UPDATE_ASSET_TELEMETRY: {
                this.updateAssetTelemetry(data[0] as AssetTelemetry[]);
                break;
            }

            case PublishTopic.WORKSPACES_RELOADED: {
                this.populateWorkspaces();
                break;
            }

            case PublishTopic.ASSET_INFO_UPDATED: {
                const assetInfo = data as AssetDTO;
                this.notifiedUpdateAssetInfo(assetInfo);
                break;
            }
        }
    }

    private notifiedUpdateAssetInfo(assetInfo: AssetDTO) {
        const data = this.tree.getRowData(assetInfo.id.id);
        data.col_vehicle = assetInfo.id.name;
        this.tree.setRowData(assetInfo.id.id, data);
    }

    /** Set the data for the asset row in the tree.
     *
     * @param asset The asset data for the row.
     * @param telemetry The telemetry data for the row.
     * @returns
     */
    private setAssetData(asset: AssetDTO | undefined, telemetry: AssetTelemetry) {
        if (asset == undefined) {
            return;
        }
        const data = this.tree.getRowData(asset.id.id);
        const odoOffsetKm = asset.odoOffset != null ? asset.odoOffset : 0;
        const hoursOffset = asset.hoursOffset != null ? asset.hoursOffset : 0;

        data.col_speed = this.parent.app.km2miles(telemetry.speed).toFixed(0);
        data.col_odometer = this.parent.app.km2miles(telemetry.odometer + odoOffsetKm).toFixed(0);
        data.col_hours = parseFloat((telemetry.hours != null ? telemetry.hours + hoursOffset : hoursOffset).toFixed(0));
        data.col_description = asset.description;

        if (telemetry.state == MotionState.MOVING) {
            data.col_status = SVGIcons.right_arrow('', 22, 18);
        } else if (telemetry.state == MotionState.STOPPED) {
            data.col_status = SVGIcons.square('#FF0000', 12, 12);
        } else if (telemetry.state == MotionState.IDLE) {
            data.col_status = SVGIcons.idle('', 20, 20);
        } else if (telemetry.state == MotionState.OFF) {
            data.col_status = SVGIcons.square('#999999', 12, 12);
        }

        if (asset?.config?.oos == true) {
            data.col_status = SVGIcons.triangle('rgb(255, 145, 0)', 18, 18);
        }

        data.col_input1 = telemetry.input1 == true ? "<B style='color:green;'>ON</B>" : 'OFF';
        data.col_input2 = telemetry.input2 == true ? "<B style='color:green;'>ON</B>" : 'OFF';
        data.col_input3 = telemetry.input3 == true ? "<B style='color:green;'>ON</B>" : 'OFF';
        data.col_input4 = telemetry.input4 == true ? "<B style='color:green;'>ON</B>" : 'OFF';

        if (telemetry.analog1 != undefined) {
            data.col_analog1 = telemetry.analog1;
        }
        if (telemetry.analog2 != undefined) {
            data.col_analog2 = telemetry.analog2;
        }
        if (telemetry.analog3 != undefined) {
            data.col_analog3 = telemetry.analog3;
        }
        if (telemetry.analog4 != undefined) {
            data.col_analog4 = telemetry.analog4;
        }

        if (telemetry.cameraImage != undefined) {
            data.col_camera = '/icons/camera.png';
        } else {
            data.col_camera = '';
        }

        if (asset) {
            if (telemetry.p != undefined) {
                data.col_plowing = telemetry.p == true ? "<B style='color:green;'>YES</B>" : "<B style='color:red;'></B>";
            }
            if (telemetry.m != undefined) {
                data.col_mowing = telemetry.m == true ? "<B style='color:green;'>YES</B>" : "<B style='color:red;'></B>";
            }
            if (telemetry.r != undefined) {
                data.col_rocking = telemetry.r == true ? "<B style='color:green;'>YES</B>" : "<B style='color:red;'></B>";
            }
            if (telemetry.s != undefined) {
                data.col_spreading = telemetry.s == true ? "<B style='color:green;'>YES</B>" : "<B style='color:red;'></B>";
            }
            if (telemetry.g != undefined) {
                data.col_grading = telemetry.g == true ? "<B style='color:green;'>YES</B>" : "<B style='color:red;'></B>";
            }
        }

        const date = new Date(telemetry.lastSeen);

        //var str = date.toLocaleString('en-US', {year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',   hour12: true, timeZone: 'America/Chicago' });
        //data.col_last_seen  = str;
        data.col_last_seen = this.formatDate(date);
        data.col_address = telemetry.address ? telemetry.address : '';
        data.col_posted = telemetry.postedSpeed ? Measurement.km2miles(telemetry.postedSpeed).toFixed(0) + ' mph' : '';

        this.tree.setRowData(telemetry.id, data);
        this.tree.setUserData(telemetry.id, 'timestamp', telemetry.lastSeen);
        this.tree.setUserData(telemetry.id, 'state', this.stateToInt(telemetry.state));
    }

    private updateAssetTelemetry(assets: AssetTelemetry[], _doZoom = false) {
        assets.forEach((item) => {
            try {
                const data = this.tree.getRowData(item.id);
                if (item.spreader != null) {
                    this.updateSpreaderData(data, item.spreader);
                }
                this.parent.app.assetService
                    .getAssetMap()
                    .then((assetMap) => assetMap.get(item.id))
                    .then((asset) => {
                        this.setAssetData(asset, item);
                    });
            } catch (error) {
                console.log(error);
            }
        });
    }

    /** Updates the spreader columns in the table.
     *
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private updateSpreaderData(data: any, spreader: SpreaderTelemetry): void {
        data.col_spreader_solid_act = Measurement.solidRate(spreader?.solidActualRate, false).toFixed(0);
        data.col_spreader_solid_set = Measurement.solidRate(spreader?.solidSetRate, false).toFixed(0);
        data.col_spreader_prewet_act = Measurement.liquidRate(spreader?.prewetActualRate, false).toFixed(0);
        data.col_spreader_prewet_set = Measurement.liquidRate(spreader?.prewetSetRate, false).toFixed(0);
        data.col_spreader_antiice_act = Measurement.liquidRate(spreader?.antiiceActualRate, false).toFixed(0);
        data.col_spreader_antiice_set = Measurement.liquidRate(spreader?.antiiceSetRate, false).toFixed(0);

        data.col_spreader_solid_mode = spreader?.solidMode;
        data.col_spreader_prewet_mode = spreader?.prewetMode;
        data.col_spreader_antiice_mode = spreader?.antiiceMode;

        // TODO : Fix material names with @ in them
        //if (spreader?.solidMaterial.indexOf('@') == -1) data.col_spreader_solid_material = spreader?.solidMaterial;
        data.col_spreader_solid_material = spreader?.solidMaterial;
        data.col_spreader_prewet_material = spreader?.prewetMaterial;
        data.col_spreader_antiice_material = spreader?.antiiceMaterial;

        data.col_spreader_seasonal_solid_1 = Measurement.mass(spreader?.seasonalSolidTotal1, false).toFixed(0);
        data.col_spreader_seasonal_solid_2 = Measurement.mass(spreader?.seasonalSolidTotal2, false).toFixed(0);
        data.col_spreader_seasonal_prewet_1 = Measurement.volume(spreader?.seasonalPrewetTotal1, false).toFixed(0);
        data.col_spreader_seasonal_prewet_2 = Measurement.volume(spreader?.seasonalPrewetTotal2, false).toFixed(0);
        data.col_spreader_seasonal_antiice_1 = Measurement.volume(spreader?.seasonalPrewetTotal1, false).toFixed(0);
        data.col_spreader_seasonal_antiice_2 = Measurement.volume(spreader?.seasonalAntiiceTotal2, false).toFixed(0);
    }

    private stateToInt(state: string) {
        switch (state) {
            case 'MOVING':
                return 0;
            case 'IDLE':
                return 1;
            case 'STOPPED':
                return 2;
            case 'OFF':
                return 3;
            default:
                return 99;
        }
    }

    private formatDate(assetdate: Date) {
        const dateObj = new Date(assetdate);
        const date = dateObj.toLocaleString('en-US', { hour12: true, timeZone: 'America/Chicago' }).split(' ');
        const time = date[1];
        const mdy = date[0];

        // We then parse  the mdy into parts
        const mdySplit = mdy.split('/');
        const month = parseInt(mdySplit[0]);
        const day = parseInt(mdySplit[1]);
        const year = parseInt(mdySplit[2]);

        // Putting it all together
        const formattedDate = time + ' ' + date[2] + '&nbsp;&nbsp;' + month + '/' + day + '/' + year;
        return formattedDate;
    }

    public editAsset(assetId: string) {
        const window = new VisionAssetWindow(this.parent.app.windowManager);
        window.selectAsset(assetId);
        window.show();
    }

    private selectWorkspace(id: string | null) {
        if (id == null) {
            return;
        }

        localStorage.setItem('selected_workspace', id);

        const workspace = this.parent.app.workspaceService.workspaces.get(id);
        if (workspace == null || workspace == undefined) {
            return;
        }

        for (let col = 0; col < this.columns.length; col++) {
            this.tree.setColumnHidden(col, true);
        }

        if (workspace) {
            this.tree.setColumnOrder([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]);

            const config = workspace.config;
            //const movement = 0;

            config.columns.forEach((col_id) => {
                const idx = this.defaultColumn(col_id);
                this.tree.setColumnHidden(Number(idx), false);
            });

            this.tree.setColumnOrder(config.order);

            if (config.widths != null) {
                for (let i = 0; i < config.widths.length; i++) {
                    const width = config.widths[i];
                    const columnId = this.tree.getColIndexById(config.columns[i]);
                    this.tree.setColWidth(columnId, String(width));
                }
            }
        }
        if (this.parent.mapPanel != null) {
            VisionApplication.getInstance().setMapWorkspace(this.parent.mapPanel.map.map);
        }
    }

    private defaultColumn(id: string | number): number | string {
        for (const col of this.columns) {
            if (col.id == id) {
                return col.idx;
            }
        }
        return 0;
        /*
        for (let i = 0; i < this.columns.length; i++) {
            const col = this.columns[i];
            if (col.id == id) {
                return col.idx;
            }
        }
        return 0;
        */
    }

    private showCameraWindow(_assetId: string): void {
        //const cameraWindow = new CameraImageWindow(this.parent.app.windowManager);
        //cameraWindow.loadAsset(assetId);
    }

    public getColumnWidths(): number[] {
        const widths = Array<number>();
        const number = this.tree.getColumnsNum();
        for (let i = 0; i < number; i++) {
            const hidden = this.tree.isColumnHidden(i);
            if (hidden == false) {
                widths.push(this.tree.getColWidth(i));
            }
        }

        return widths;
    }

    private selectColumns() {
        const win = new dhtmlXWindows().createWindow('colChooser', 100, 100, 300, 400);
        win.setText('Select Columns');
        const colTree = win.attachTree();
        colTree.enableCheckBoxes(true, false);
        colTree.setIconsPath('/css/imgs/');
        colTree.setImagesPath('/css/imgs/dhxtree_material/');

        colTree.insertNewItem(0, 'telemetry', 'Telemetry', undefined, undefined, undefined, undefined, undefined, false);
        colTree.insertNewItem(0, 'inputs', 'Inputs', undefined, undefined, undefined, undefined, undefined, false);
        colTree.insertNewItem(0, 'activities', 'Activities', undefined, undefined, undefined, undefined, undefined, false);
        colTree.insertNewItem(0, 'spreader', 'Spreader', undefined, undefined, undefined, undefined, undefined, false);

        colTree.attachEvent('onCheck', (id, state) => {
            const colIndex = this.tree.getColIndexById(id);
            const childrenString = colTree.getSubItems(id);
            if (childrenString.length > 0) {
                const children = String(childrenString).split(',');
                children.forEach((childid) => {
                    const childIndex = this.tree.getColIndexById(childid);
                    colTree.setCheck(childid, !state == true ? 0 : 1);
                    this.tree.setColumnHidden(childIndex, !state);
                });
            }

            this.tree.setColumnHidden(colIndex, !state);
        });

        this.columns.forEach((item) => {
            let parent = item.parent;
            if (parent == undefined) {
                parent = 'telemetry';
            }
            const columnId = item.id;

            colTree.insertNewItem(parent, columnId, item.label, undefined, undefined, undefined, undefined, undefined, false);
            const colIndex = this.tree.getColIndexById(columnId);
            if (this.tree.isColumnHidden(colIndex) === false) {
                colTree.setCheck(columnId, 1);
            }
        });
    }
}
